// media-cdn.interceptor.ts
//
// Repoints stored media URLs at the CDN host as responses arrive, so the admin
// stops pulling product imagery from the expensive origin bucket.
//
// Applied in the ONE place every API response passes through rather than in the
// image components: media URLs arrive as data and are rendered from dozens of
// places (product tables, variant editors, page-builder previews, order detail),
// so a component-level rewrite would miss nearly everything.
//
// The rewrite itself is a no-op unless BOTH `mediaCdnBaseUrl` and
// `mediaLegacyBaseUrls` are set at build time, so this is inert until the
// objects have actually been mirrored. See `core/utils/to-cdn-url.ts` for the
// allowlist and why it refuses invoice/report/credit-note URLs.

import { Injectable } from '@angular/core';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { isCdnRewriteEnabled, rewriteMediaUrlsDeep } from '../utils/to-cdn-url';

@Injectable()
export class MediaCdnInterceptor implements HttpInterceptor {
  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Cheapest possible exit when the feature is off — no operator is added to
    // the stream at all, so an unconfigured build pays nothing.
    if (!isCdnRewriteEnabled()) return next.handle(req);

    // Only JSON bodies carry media URLs as data. Blob/arraybuffer responses are
    // the actual file downloads (invoice PDFs, report exports) and must not be
    // walked — they are not objects we can or should traverse.
    if (req.responseType !== 'json') return next.handle(req);

    return next.handle(req).pipe(
      map((event: HttpEvent<any>) => {
        if (!(event instanceof HttpResponse)) return event;
        const body = event.body;
        if (!body || typeof body !== 'object') return event;

        const rewritten = rewriteMediaUrlsDeep(body);
        // rewriteMediaUrlsDeep returns the SAME reference when nothing matched,
        // so an untouched payload skips the clone entirely.
        return rewritten === body ? event : event.clone({ body: rewritten });
      })
    );
  }
}
